import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'
import { Nullable } from '@types'

import CustomColorTooltip from '@components/CustomColorTooltip'
import CustomPopover from '@components/CustomPopover'
import { CssTruncation } from '@components/TruncateLines/index.styles'
import LockIcon from '@components/icons/LockIcon'
import { UserEventsOptions } from '@modules/search/types'
import BlockIcon from '@mui/icons-material/Block'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import useSelection from '@shared/hooks/useSelection'
import { colors } from '@theme/variables'

import {
  EventSelectFilterWrapper,
  FilterButton,
  FilterExpandIcon,
  FilterWrapper,
  VerticalDivider,
} from './EventSelectFilter.styles'

interface EventSelectFilterProps {
  eventsOptions: UserEventsOptions[]
  onSubmit: (data: { includeIds: number[]; excludeIds: number[] }) => void
  eventsFilterData?: { includeIds?: number[]; excludeIds?: number[] } | null
}

const EventItemRow = ({
  event,
  isSelected,
  isExcluded,
  onSelect,
  onExclude,
}: {
  event: UserEventsOptions
  isSelected: boolean
  isExcluded: boolean
  onSelect: (id: number) => void
  onExclude: (id: number) => void
}) => {
  const { t } = useTranslation()
  const itemStyle = isExcluded ? { color: '#B2B2B2' } : {}

  return (
    <Box key={event.value} display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
      <Checkbox
        checked={isSelected && !isExcluded}
        onChange={() => onSelect(event.value)}
        disabled={isExcluded}
        style={{ ...itemStyle, padding: '0 9px' }}
      />
      <Box flexGrow={1} display="flex" gap="5px" alignItems="center">
        <Typography variant="body1" style={itemStyle}>
          {event.label}
        </Typography>
        {event.hidden ? (
          <CustomColorTooltip
            placement="top"
            arrow
            title={t(translationKeys.SEARCH_FILTER_BY_EVENT_TOOLTIP)}
            bgColor={colors.primary}
            maxWidth="none"
          >
            <Box display="flex" marginTop="-2px">
              <LockIcon color="primary" style={{ width: '17px' }} />
            </Box>
          </CustomColorTooltip>
        ) : null}
      </Box>
      <IconButton
        onClick={() => onExclude(event.value)}
        color={isExcluded ? 'primary' : 'default'}
        size="small"
        style={{
          color: isExcluded ? colors.primary : '#B2B2B2',
          padding: '5px 0px 5px 0px',
        }}
      >
        <BlockIcon />
      </IconButton>
    </Box>
  )
}

const EventSelectFilter = ({ eventsOptions, onSubmit, eventsFilterData }: EventSelectFilterProps) => {
  const { t } = useTranslation()
  const [anchorEl, setAnchorEl] = useState<Nullable<HTMLElement>>(null)
  const [confirmedSelectedIds, setConfirmedSelectedIds] = useState<number[]>(eventsFilterData?.includeIds ?? [])

  const {
    selectedIds: includeEventIds,
    toggleSelectItem: toggleIncludeEvent,
    toggleSelectAll,
    clearSelectedItems,
  } = useSelection<number>(
    eventsOptions.map(option => option.value),
    { initiallySelectedItems: eventsFilterData?.includeIds || eventsOptions.map(option => option.value) },
  )

  const {
    selectedIds: excludeEventIds,
    toggleSelectItem: toggleExcludeEvent,
    clearSelectedItems: clearExcludeItems,
  } = useSelection<number>(
    eventsOptions.map(option => option.value),
    { initiallySelectedItems: eventsFilterData?.excludeIds },
  )

  const handleOpenPopover = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    clearSelectedItems()
    clearExcludeItems()
    setAnchorEl(null)
  }

  const handleSelect = (id: number) => {
    if (!excludeEventIds.includes(id)) {
      toggleIncludeEvent(id)
    }
  }

  const handleExclude = (id: number) => {
    if (includeEventIds.includes(id)) {
      toggleIncludeEvent(id)
    }
    toggleExcludeEvent(id)
  }

  const isAllSelected = eventsOptions.length === includeEventIds.length

  const onConfirm = () => {
    setConfirmedSelectedIds(includeEventIds)
    onSubmit({ includeIds: includeEventIds, excludeIds: excludeEventIds })
    handleClose()
  }

  const shouldDisableConfirm = useMemo(
    () =>
      eventsOptions.every(({ value }) => excludeEventIds.includes(value)) || (!includeEventIds.length && !excludeEventIds.length),
    [includeEventIds, excludeEventIds],
  )
  useEffect(() => {
    if (eventsFilterData && eventsFilterData.includeIds) {
      const selectedEvents = eventsOptions.filter(event => eventsFilterData.includeIds?.includes(event.value))
      if (selectedEvents.length) {
        setConfirmedSelectedIds(selectedEvents.map(event => event.value))
      }
    } else {
      setConfirmedSelectedIds([])
    }
  }, [eventsFilterData, eventsOptions])

  const selectedStateLabel = useMemo(() => {
    const nonExcludedEvents = eventsOptions.filter(event => !excludeEventIds.includes(event.value))
    const includedEvents = confirmedSelectedIds.filter(id => !excludeEventIds.includes(id))

    if (isAllSelected && nonExcludedEvents.length === 1) {
      return nonExcludedEvents[0].label
    }
    if (includedEvents.length > 1) {
      return t(translationKeys.FILTER_BY_MULTIPLE)
    }
    if (includedEvents.length === 1) {
      const event = eventsOptions.find(option => option.value === includedEvents[0])
      return event ? event.label : ''
    }
    return ''
  }, [confirmedSelectedIds, excludeEventIds, isAllSelected, eventsOptions, t])

  return (
    <EventSelectFilterWrapper>
      <FilterWrapper isConfirmed={confirmedSelectedIds.length > 0}>
        <FilterButton
          onClick={handleOpenPopover}
          isConfirmed={confirmedSelectedIds.length > 0}
          endIcon={<FilterExpandIcon isConfirmed={confirmedSelectedIds.length > 0} />}
        >
          {confirmedSelectedIds.length > 0 ? t(translationKeys.SEARCH_BY_EVENT) : t(translationKeys.DEFAULT_BY_EVENT)}
        </FilterButton>
        {confirmedSelectedIds.length > 0 && (
          <>
            <VerticalDivider orientation="vertical" variant="middle" flexItem />
            <Box maxWidth="150px">
              <CssTruncation>{selectedStateLabel}</CssTruncation>
            </Box>
          </>
        )}
      </FilterWrapper>

      <CustomPopover
        id="event-popover"
        anchor={anchorEl}
        onClose={handleClose}
        setAnchor={setAnchorEl}
        sx={{
          '.MuiPopover-paper': {
            maxHeight: '430px',
            width: '377px',
            overflow: 'hidden',
            padding: '24px 16px 16px 16px',
            marginTop: '10px',
          },
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" flexGrow={1} alignItems="center">
            <Checkbox checked={isAllSelected} onChange={toggleSelectAll} />
            <Typography variant="body1">{t(translationKeys.FILTER_BY_ALL)}</Typography>
          </Box>
          <Typography variant="body1">{t(translationKeys.FILTER_BY_EXCLUDE)}</Typography>
        </Box>
        <Box
          sx={{
            maxHeight: '210px',
            overflow: 'auto',
          }}
        >
          {eventsOptions.map(event => (
            <EventItemRow
              event={event}
              isSelected={includeEventIds.includes(event.value)}
              isExcluded={excludeEventIds.includes(event.value)}
              onSelect={handleSelect}
              onExclude={handleExclude}
            />
          ))}
        </Box>
        <Box mt={2} display="flex" justifyContent="flex-end">
          <Button variant="outlined" onClick={handleClose} sx={{ mr: 1 }}>
            {t(translationKeys.CANCEL_BUTTON)}
          </Button>
          <Button variant="contained" onClick={onConfirm} disabled={shouldDisableConfirm}>
            {t(translationKeys.SEARCH_FILTER_BY_DATE_SUBMIT)}
          </Button>
        </Box>
      </CustomPopover>
    </EventSelectFilterWrapper>
  )
}

export default EventSelectFilter
